import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import Toggle from 'react-toggle';
import intlShape from '@/shared/utils/intlShape';

import ZendeskHelp from '@/shared/components/ZendeskHelp';
import SimplePanel from '@/shared/components/SimplePanel';
import handleHttpError from '@/shared/services/HandleHttpError';
import FormattedListValue, { listItemMsg } from '@/shared/components/FormattedListValue';
import { hasMultipleCompanies } from '@/shared/components/security/HasMultiCompany';
import EditableCompanyMultiSelect from '@/shared/components/editable/EditableCompanyMultiSelect';

import api from '@/shared/api';
import config from '@/config';
import LoadingAnimation from '@/shared/components/LoadingAnimation';
import ToastrService from '@/shared/services/ToastrService';

import { dynamicSort } from '@/shared/utils/dynamicSort';
import DataCacheService from '@/shared/services/DataCacheService';
import messages from './notificationMessages';
import employeeProfileMessages from '@/shared/messages/employeeProfileMessages';

const mapSettings = data => {
  // Don't map  notificationTypes prop
  const { notificationTypes, ...settings } = data || {};

  return {
    allRelations: true,
    relationResponsible: true,
    relationAssistant: true,
    relationAssistantBackup: true,
    relationAssociate: true,
    relationBillingUser: true,
    relationSeniorUser: true,
    ...settings,
  };
};

class NotificationSettingsContainer extends Component {
  state = {
    settings: null,
    saving: false,
    profiles: [],
  };

  componentDidMount() {
    const { intl } = this.props;
    api.base.get(`${config.appUrls.notifications}/api/Settings`).then(({ data = {} }) => {
      const { notificationTypes = [] } = data;

      const groupedNotificationTypes = notificationTypes
        .map(x => ({
          ...x,
          label: intl.formatMessage(listItemMsg('notificationtypelabel', x.type)),
        }))
        .sort(dynamicSort('label'))
        .reduce((result, currentItem) => {
          const key = currentItem.category;
          if (!result[key]) {
            result[key] = [];
          }

          result[key].push(currentItem);
          return result;
        }, {});

      this.setState({
        settings: mapSettings(data),
        groupedNotificationTypes,
        ownCompanies: DataCacheService.getDataCache().company,
      });
    });
    const profiles = [
      {
        id: 'relationResponsible',
        label: intl.formatMessage(employeeProfileMessages.Responsible),
      },
      {
        id: 'relationAssistant',
        label: intl.formatMessage(employeeProfileMessages.Assistant),
      },
      {
        id: 'relationAssistantBackup',
        label: intl.formatMessage(employeeProfileMessages.AssistantBackup),
      },
      {
        id: 'relationAssociate',
        label: intl.formatMessage(employeeProfileMessages.Associate),
      },
      {
        id: 'relationBillingUser',
        label: intl.formatMessage(employeeProfileMessages.BillingUser),
      },
      {
        id: 'relationSeniorUser',
        label: intl.formatMessage(employeeProfileMessages.SeniorUser),
      },
    ].sort(dynamicSort('label'));
    this.setState({ profiles });
  }

  updateSetting = values => {
    const { settings, saving } = this.state;
    if (saving) return;
    this.setState({ saving: true });

    const model = { ...settings, ...values };
    if (JSON.stringify(model) === JSON.stringify(settings)) return; // Not Changed
    return api.base.put(`${config.appUrls.notifications}/api/Settings`, model).then(
      ({ data = {} }) => {
        this.setState({
          settings: mapSettings(data),
          saving: false,
        });
        const { intl } = this.props;
        ToastrService.changesSaved(intl, null, true);
        return settings;
      },
      error => {
        const { intl } = this.props;
        handleHttpError(intl)(error);
        this.setState({ saving: false });
      },
    );
  };

  updateAllRelations = checked => {
    if (checked) {
      this.updateSetting({
        allRelations: true,
        relationResponsible: false,
        relationAssistant: false,
        relationAssistantBackup: false,
        relationAssociate: false,
        relationBillingUser: false,
        relationSeniorUser: false,
      });
    } else {
      this.updateSetting({
        allRelations: false,
      });
    }
  };

  updateRelationProfile = (prop, checked) => {
    if (!checked) {
      this.updateSetting({
        allRelations: false,
        [prop]: false,
      });
    } else {
      this.updateSetting({
        [prop]: true,
      });
    }
  };
  updateOwnCompanies = (id, checked) => {
    const { settings, ownCompanies } = this.state;

    // if this is the first time modifying the own company setting, the current value will be null and we should send all the other (enabled) companies.
    let selectedCompanies = [];
    if (!settings.relationsOfCompaniesCommaSeparated) {
      if (checked) {
        selectedCompanies = ownCompanies.filter(x => x.id === id).map(x => x.id);
      } else {
        selectedCompanies = ownCompanies.filter(x => x.id !== id).map(x => x.id);
      }
    } else {
      const currentlySelectedCompanies = settings.relationsOfCompanies;
      // if this is the last company that is being disabled, toggle the general setting instead.
      if (
        currentlySelectedCompanies.length === 1 &&
        currentlySelectedCompanies[0] === id &&
        !checked
      ) {
        this.updateAllRelations(false);
        return;
      }
      if (checked) {
        selectedCompanies = [...currentlySelectedCompanies, id];
      } else {
        selectedCompanies = currentlySelectedCompanies.filter(x => x !== id);
      }
    }
    this.updateSetting({
      relationsOfCompanies: selectedCompanies,
    });
  };

  updateNotificationSetting = (type, value, category) => {
    const { saving } = this.state;
    if (saving) return;
    this.setState({ saving: true });
    const model = { type, shouldCreate: !!value };
    return api.base.put(`${config.appUrls.notifications}/api/Settings/Type`, model).then(
      () => {
        const { groupedNotificationTypes } = this.state;
        const modified = {
          ...groupedNotificationTypes,
          [category]: groupedNotificationTypes[category].map(x =>
            x.type !== type ? x : { ...x, shouldCreate: model.shouldCreate },
          ),
        };

        this.setState({ groupedNotificationTypes: modified, saving: false });
        const { intl } = this.props;
        ToastrService.changesSaved(intl, null, true);
      },
      error => {
        const { intl } = this.props;
        handleHttpError(intl)(error);
        this.setState({ saving: false });
      },
    );
  };

  render() {
    const { settings, profiles, groupedNotificationTypes, ownCompanies } = this.state;
    const { intl } = this.props;
    if (!settings) return <LoadingAnimation />;

    const categories = Object.keys(groupedNotificationTypes);
    const half = Math.ceil((categories || []).length / 2);
    const categoriesCol1 = categories ? categories.slice(0, half) : [];
    const categoriesCol2 = categories ? categories.slice(half) : [];
    const hasMultiCompanies = hasMultipleCompanies();
    const ownCompaniesCol1 = ownCompanies
      ? ownCompanies.slice(0, Math.ceil((ownCompanies || []).length / 2))
      : [];
    const ownCompaniesCol2 = ownCompanies
      ? ownCompanies.slice(Math.ceil((ownCompanies || []).length / 2))
      : [];

    return (
      <div>
        <SimplePanel
          article="5434869892242"
          title={intl.formatMessage(messages.NotificationSettings)}
        >
          <div className="info-block container-fluid radius4B">
            <div className="row">
              <div className="col-md-12">
                <label className="react-toggle-wrapper margin5T">
                  <Toggle
                    checked={settings.allRelations}
                    onChange={e => this.updateAllRelations(e.target.checked)}
                  />

                  {hasMultiCompanies ? (
                    <FormattedMessage
                      className="label-text"
                      {...messages.ReceiveNotificationsForAllRelationsOfCompanies}
                    />
                  ) : (
                    <FormattedMessage
                      className="label-text"
                      {...messages.ReceiveNotificationsForAllRelations}
                    />
                  )}
                </label>
              </div>
            </div>

            <div className="row">
              {hasMultiCompanies ? (
                <>
                  <div className="col-md-2">
                    {ownCompaniesCol1.map(({ id, name }) => (
                      <div key={id}>
                        <label className="react-toggle-wrapper margin5T">
                          <Toggle
                            checked={
                              // since this setting was added later and the default is 'all companies', we will show the toggles as enabled if the value is empty but the all relations setting is enabled
                              settings.allRelations
                                ? settings.relationsOfCompanies.length > 0
                                  ? settings.relationsOfCompanies.includes(id)
                                  : true
                                : false
                            }
                            onChange={e => this.updateOwnCompanies(id, e.target.checked)}
                            disabled={!settings.allRelations}
                          />
                          <span className="label-text">{name}</span>
                        </label>
                      </div>
                    ))}
                  </div>
                  <div className="col-md-2">
                    {ownCompaniesCol2.map(({ id, name }) => (
                      <div key={id}>
                        <label className="react-toggle-wrapper margin5T">
                          <Toggle
                            checked={
                              // since this setting was added later and the default is 'all companies', we will show the toggles as enabled if the value is empty but the all relations setting is enabled
                              settings.allRelations
                                ? settings.relationsOfCompanies.length > 0
                                  ? settings.relationsOfCompanies.includes(id)
                                  : true
                                : false
                            }
                            onChange={e => this.updateOwnCompanies(id, e.target.checked)}
                            disabled={!settings.allRelations}
                          />
                          <span className="label-text">{name}</span>
                        </label>
                      </div>
                    ))}
                  </div>
                </>
              ) : null}
            </div>
            <div className="row">
              <div className="col-md-12">
                <div className="margin5B">
                  <FormattedMessage className="label-text" {...messages.OrOnlyIfYouAreAssignedAs} />
                </div>
                {profiles &&
                  profiles.map(({ id, label }) => (
                    <div key={id}>
                      <label className="react-toggle-wrapper margin5T">
                        <Toggle
                          checked={settings[id]}
                          onChange={e => this.updateRelationProfile(id, e.target.checked)}
                          disabled={settings.allRelations}
                        />
                        <span className="label-text">{label}</span>
                      </label>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </SimplePanel>
        {groupedNotificationTypes && Object.keys(groupedNotificationTypes).length > 0 ? (
          <SimplePanel title={intl.formatMessage(messages.NotificationTypeSettings)}>
            <div className="info-block container-fluid radius4B">
              <div className="margin5B">
                <FormattedMessage {...messages.NotificationTypeSettingsInfo} />
              </div>
              <div className="row">
                <div className="col-md-6">
                  {categoriesCol1.map(category => (
                    <div key={category}>
                      <h2>
                        <FormattedListValue
                          list="notificationcategory"
                          id={category}
                          fallback={category}
                        />
                      </h2>
                      <div>
                        {groupedNotificationTypes[category].map(({ type, label, shouldCreate }) => (
                          <div key={type}>
                            <label className="react-toggle-wrapper margin5T">
                              <Toggle
                                checked={shouldCreate}
                                onChange={e =>
                                  this.updateNotificationSetting(type, e.target.checked, category)
                                }
                              />
                              <span className="label-text">{label}</span>
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="col-md-6">
                  {categoriesCol2.map(category => (
                    <div key={category}>
                      <h2>
                        <FormattedListValue
                          list="notificationcategory"
                          id={category}
                          fallback={category}
                        />
                      </h2>
                      <div>
                        {groupedNotificationTypes[category].map(({ type, label, shouldCreate }) => (
                          <div key={type}>
                            <label className="react-toggle-wrapper margin5T">
                              <Toggle
                                checked={shouldCreate}
                                onChange={e =>
                                  this.updateNotificationSetting(type, e.target.checked, category)
                                }
                              />
                              <span className="label-text">{label}</span>
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </SimplePanel>
        ) : null}
      </div>
    );
  }
}

NotificationSettingsContainer.propTypes = {
  intl: intlShape.isRequired,
};

export default injectIntl(NotificationSettingsContainer);
